/* eslint-disable camelcase */
// import { LoadingOutlined } from '@ant-design/icons';

import { useEffect, useState } from 'react';

import { Button } from 'antd';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import AsyncBulkUpload from './AsyncBulkUpload';
import { onError, POLLING_FREQ, UI_JOB_STATUS } from './BulkUploaderUtils';
import ROInfoMessage from './ROInfoMessage';
import apiConfig from '../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../API_Utils/APIUtil/CallApi';
import { delay, getError, LINKS, validator } from '../../clientUtils';
import { FILE_TYPE_ATTRIBUTE } from '../../constants';
import { showMessage } from '../../MessageHook';

const JOB_NAMES = {
  createValidation: 'POSI_RETURN_ORDER_CREATE_VALIDATE',
  createSubmit: 'POSI_RETURN_ORDER_CREATE_SUBMIT',
  updateValidation: 'POSI_RETURN_ORDER_UPDATE_VALIDATE',
  updateSubmit: 'POSI_RETURN_ORDER_UPDATE_SUBMIT'
};

const SAMPLE_TYPES = {
  skuOffline: 'BULK_RO_DOMESTIC_OFFLINE_SKU_CREATE_SAMPLE_FILE',
  skuOnline: 'BULK_RO_DOMESTIC_ONLINE_SKU_CREATE_SAMPLE_FILE',
  doc: 'BULK_RO_DOCUMENT_CREATE_INPUT_FILE',
  refurbishInternal: 'BULK_RO_REFURBISH_INTERNAL_SKU_CREATE_SAMPLE_FILE',
  refurbishExternal: 'BULK_RO_REFURBISH_EXTERNAL_SKU_CREATE_SAMPLE_FILE'
};

const FILE_TYPES = {
  docCreateInput: 'BULK_RO_DOCUMENT_CREATE_INPUT_FILE',
  SKUCreateInput: 'BULK_RO_SKU_CREATE_INPUT_FILE',
  docCreateValidate: 'BULK_RO_DOCUMENT_CREATE_VALIDATION_FILE',
  SKUCreateValidate: 'BULK_RO_SKU_CREATE_VALIDATION_FILE',
  docCreateSubmit: 'BULK_RO_DOCUMENT_CREATE_SUBMIT_FILE',
  SKUCreateSubmit: 'BULK_RO_SKU_CREATE_SUBMIT_FILE'
};

const TYPES = {
  DOMESTIC_ONLINE: 'DOMESTIC_ONLINE_RETURN',
  DOMESTIC_OFFLINE: 'DOMESTIC_OFFLINE_RETURN',
  'refurbish-internal': 'REFURBISH_INTERNAL',
  'refurbish-external': 'REFURBISH_EXTERNAL'
};

const JOB_STATUS = {
  created: 'CREATED',
  started: 'STARTED',
  success: 'SUCCESS',
  errorOnExecution: 'ERROR_ON_EXECUTION',
  warning: 'WARNING',
  submitted: 'SUBMITTED',
  failed: 'FAILED'
};

const JOB_STATUS_MAPPING = {
  CREATED: 'loading',
  STARTED: 'loading',
  SUCCESS: 'end',
  ERROR_ON_EXECUTION: 'end',
  WARNING: 'end',
  SUBMITTED: 'end',
  FAILED: 'end'
};

const getSampleFileDownloadParams = (ROType, fileType) => {
  const map = {
    DOMESTIC_OFFLINE: {
      sku: 'skuOffline',
      doc: 'doc'
    },
    DOMESTIC_ONLINE: {
      sku: 'skuOnline',
      doc: 'doc'
    },
    'refurbish-internal': {
      sku: 'refurbishInternal',
      doc: 'doc'
    },
    'refurbish-external': {
      sku: 'refurbishExternal',
      doc: 'doc'
    }
  };
  return SAMPLE_TYPES[map[ROType][fileType]];
};

const getJobType = (status, jobName, validationSummary) => {
  if (status === JOB_STATUS.errorOnExecution) return UI_JOB_STATUS.errorOnExecution;

  const nonTerminalStatus = [JOB_STATUS.created, JOB_STATUS.started];

  if (jobName.includes('VALIDATE')) {
    if (nonTerminalStatus.includes(status)) return UI_JOB_STATUS.validating;
    let validationFailed = false;
    validationSummary.forEach((item) => {
      if (item.failure > 0) {
        validationFailed = true;
      }
    });

    if (validationFailed) return UI_JOB_STATUS.validationFailed;
    return UI_JOB_STATUS.validated;
  }

  if (jobName.includes('SUBMIT')) {
    if (nonTerminalStatus.includes(status)) return UI_JOB_STATUS.submitting;
    return UI_JOB_STATUS.submitted;
  }

  return null;
};

const getCTAText = (jobStatus) => {
  const RUN_VALIDATION = [
    UI_JOB_STATUS.uploading,
    UI_JOB_STATUS.uploaded,
    UI_JOB_STATUS.validating,
    UI_JOB_STATUS.validationFailed
  ];
  if (RUN_VALIDATION.includes(jobStatus)) return 'Run Validation';
  return 'Submit';
};

const title = 'Return Order Bulk Create';

const ROCreate = () => {
  const { type, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [sampleDownloadLinks, setSampleDownloadLinks] = useState({});
  const [SKUFiles, setSKUFiles] = useState([]);
  const [docFiles, setDocFiles] = useState([]);
  const [s3Links, sets3Links] = useState({});
  const [buttonLoading, setButtonLoading] = useState(false);

  const [jobStatus, setJobStatus] = useState(UI_JOB_STATUS.uploading);
  const [errorMessage, setErrorMessage] = useState(null);

  const navigate = useNavigate();

  const message = showMessage();

  // fetching sample download files
  useEffect(() => {
    CallApi(
      apiConfig.job.sampleFile,
      { upload_file_type: getSampleFileDownloadParams(type, 'doc') },
      null,
      setLoading,
      (res) => setSampleDownloadLinks((prev) => ({ ...prev, doc: res })),
      () => message.error('Unable to download sample files!')
    );
    CallApi(
      apiConfig.job.sampleFile,
      { upload_file_type: getSampleFileDownloadParams(type, 'sku') },
      null,
      setLoading,
      (res) => setSampleDownloadLinks((prev) => ({ ...prev, SKU: res })),
      () => message.error('Unable to download sample files!')
    );
  }, [message, type]);

  useEffect(() => {
    let pollingId;
    if (id) {
      const getJobStatus = () => {
        CallApi(
          { ...apiConfig.job.status, path: apiConfig.job.status.path(id) },
          null,
          null,
          null,
          (res) => {
            if (JOB_STATUS_MAPPING[res.status] === 'end') {
              if (pollingId) clearInterval(pollingId);
            }
            if (
              (res.job_name.includes('SUBMIT') && res.status === JOB_STATUS.success) ||
              res.status === JOB_STATUS.submitted
            ) {
              message.info('File submitted, redirecting to listing page');
              delay(3000, () => navigate('/sales/ro'), onError);
            }
            setJobStatus(res);
            let skuFile;
            let docFile;
            // set files
            if (res.job_name.includes('VALIDATE')) {
              skuFile = res?.files?.find((file) => file.type === FILE_TYPES.SKUCreateValidate);
              docFile = res?.files?.find((file) => file.type === FILE_TYPES.docCreateValidate);
              setSKUFiles([{ skuFile, summary: skuFile?.validationSummary, link: skuFile?.fileLink }]);
              setDocFiles([{ docFile, summary: docFile?.validationSummary, link: docFile?.fileLink }]);
            }
            if (res.job_name.includes('SUBMIT')) {
              skuFile = res?.files?.find((file) => file.type === FILE_TYPES.SKUCreateSubmit);
              docFile = res?.files?.find((file) => file.type === FILE_TYPES.docCreateSubmit);
              setSKUFiles([{ ...skuFile, summary: skuFile?.validationSummary, link: skuFile?.fileLink }]);
              setDocFiles([{ ...docFile, summary: docFile?.validationSummary, link: docFile?.fileLink }]);
            }

            // sets3links
            const links = {
              sku: skuFile?.fileLink,
              doc: docFile?.fileLink
            };
            sets3Links(links);

            setJobStatus(
              getJobType(res.status, res.job_name, [skuFile?.validationSummary, docFile?.validationSummary])
            );
            if (res.status === UI_JOB_STATUS.errorOnExecution) setErrorMessage(res?.remarks);
          },
          onError
        );
      };

      getJobStatus();

      pollingId = setInterval(getJobStatus, POLLING_FREQ);
    }
    return () => clearInterval(pollingId);
  }, [type, id, message, navigate]);

  const onUpload = (file, onSuccess) => {
    CallApi(
      apiConfig.job.getPresignedURL,
      {
        jobName: JOB_NAMES.createValidation
      },
      null,
      undefined,
      // eslint-disable-next-line no-console
      (res) => console.log(res),
      (res) => message.error(getError(res)),
      async (res) => {
        try {
          const response = await axios.put(res.data, file, {
            headers: {
              'Content-Type': file.type
            }
          });

          if (response.status === 200) {
            onSuccess(file, res.data);
          }
        } catch {
          message.error('Failed to upload file');
        }
      }
    );
  };

  const SKUListUploadProps = {
    title: 'SKU File',
    description: 'Please upload the list of SKUs (in CSV format with max file size of 5 MB)',
    sampleDownloadText: 'Download Sample file',
    sampleDownloadLink: sampleDownloadLinks?.SKU,
    accept: FILE_TYPE_ATTRIBUTE.csv,
    files: SKUFiles,
    validator,
    onUpload: (file) =>
      onUpload(file, (f, link) => {
        setSKUFiles([f]);
        sets3Links((prev) => ({ ...prev, sku: link }));
        if (s3Links.doc) setJobStatus(UI_JOB_STATUS.uploaded);
      }),
    onRemove: async () => {
      setSKUFiles([]);
      sets3Links((prev) => {
        const newState = prev;
        delete newState.sku;
        return newState;
      });
      setJobStatus(UI_JOB_STATUS.uploading);
      message.success('File deleted successfully!');
    },
    deleteDisabled: jobStatus === UI_JOB_STATUS.submitted
  };

  const docsUploadProps = {
    title: 'Document File',
    description: 'Please upload the list of SKUs (in CSV format with max file size of 5 MB)',
    sampleDownloadText: 'Download Sample file',
    sampleDownloadLink: sampleDownloadLinks?.doc,
    accept: FILE_TYPE_ATTRIBUTE.csv,
    files: docFiles,
    validator,
    onUpload: (file) =>
      onUpload(file, (f, link) => {
        setDocFiles([f]);
        sets3Links((prev) => ({ ...prev, doc: link }));
        if (s3Links.sku) setJobStatus(UI_JOB_STATUS.uploaded);
      }),
    onRemove: async () => {
      setDocFiles([]);
      sets3Links((prev) => {
        const newState = prev;
        delete newState.doc;
        return newState;
      });
      setJobStatus(UI_JOB_STATUS.uploading);
      message.success('File deleted successfully!');
    },
    deleteDisabled: jobStatus === UI_JOB_STATUS.submitted
  };

  const skuFileUploaderProps = {
    message: '',
    items: [SKUListUploadProps]
  };

  const docFileUploadProps = {
    message: '',
    items: [docsUploadProps]
  };

  const getValidationJobBody = () => {
    const body = {
      job_name: JOB_NAMES.createValidation,
      files: [
        {
          type: FILE_TYPES.SKUCreateInput,
          file_link: s3Links.sku
        },
        {
          type: FILE_TYPES.docCreateInput,
          file_link: s3Links.doc
        }
      ],
      metadata: {
        order_type: TYPES[type]
      }
    };

    return body;
  };

  const getSubmitBody = () => {
    const body = {
      job_name: JOB_NAMES.createSubmit,
      validate_job_id: id
    };
    return body;
  };

  const handleClick = () => {
    // jobStatus === uploaded -> validation job
    if (jobStatus === UI_JOB_STATUS.uploaded) {
      CallApi(apiConfig.job.validate, null, getValidationJobBody(), setButtonLoading, (res) => {
        navigate(`${LINKS.returnOrder.bulkUploader.basePath}/${type}/${res.job_id.toString()}`);
      });
      return;
    }
    // jobStatus === validated -> submission job
    CallApi(apiConfig.job.submit, null, getSubmitBody(), setButtonLoading, (res) => {
      message.success('Job submitted');
      delay(2000);
      navigate(`${LINKS.returnOrder.bulkUploader.basePath}/${type}/${res.job_id.toString()}`);
    });
  };

  const ifButtonDisabled = () => {
    if (jobStatus === UI_JOB_STATUS.validated) {
      if (docFiles[0].validationSummary?.failure > 0 || SKUFiles[0].validationSummary?.failure > 0) return true;
      return false;
    }
    if (jobStatus === UI_JOB_STATUS.uploaded) return false;
    return true;
  };

  return (
    <AsyncBulkUpload
      button={
        <Button disabled={ifButtonDisabled()} loading={buttonLoading} onClick={handleClick} type="primary">
          {getCTAText(jobStatus)}
        </Button>
      }
      info={<ROInfoMessage message={errorMessage} status={jobStatus} />}
      loading={loading}
      menuKey="RO_create"
      tag={type}
      title={title}
      uploadersProp={[skuFileUploaderProps, docFileUploadProps]}
    />
  );
};

export default ROCreate;
